<template>
	<div ref="container" class="frc-captcha" data-cy="friendly-captcha" />
</template>

<script>
import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk'

/*
 * Friendly Captcha (v2) widget wrapper - MYS-6325.
 *
 * There is no official Vue package for Friendly Captcha, so this is the vendor's recommended
 * hand-written wrapper: it owns the widget lifecycle and re-emits the widget events.
 *
 * The parent is responsible for calling reset() after a failed submit - captcha responses are
 * single use, so a re-submit with the same token is rejected as 'response_duplicate'.
 */
export default {
	name: 'FriendlyCaptcha',
	props: {
		sitekey: { type: String, required: true },
		// 'eu' keeps visitor IP addresses inside the EU, which is an ÖBB requirement.
		apiEndpoint: { type: String, default: 'eu' },
		language: { type: String, default: null },
	},
	emits: ['complete', 'expire', 'error'],
	data() { return {
		sdk: null,
		widget: null,
	}},
	mounted() {
		this.sdk = new FriendlyCaptchaSDK()
		this.widget = this.sdk.createWidget({
			element: this.$refs.container,
			sitekey: this.sitekey,
			apiEndpoint: this.apiEndpoint,
			...(this.language ? { language: this.language } : {}),
		})

		this.widget.addEventListener('frc:widget.complete', event => this.$emit('complete', event.detail.response))
		this.widget.addEventListener('frc:widget.error', event => this.$emit('error', event.detail?.error))

		/*
		 * Expiry needs care. With the default startMode of 'focus' the challenge is solved as soon
		 * as the user focuses the form, so the token starts ageing while they are still typing -
		 * and a long form can outlive it. Friendly Captcha does not document the lifetime, the
		 * widget does NOT refresh itself, and the expire event still carries the now-stale response
		 * (see FRCWidgetExpireEventData.response), so it cannot be trusted.
		 *
		 * We therefore tell the parent to drop its token AND immediately solve a new challenge, so
		 * a fresh response is always ready at submit time regardless of the actual lifetime.
		 */
		this.widget.addEventListener('frc:widget.expire', () => {
			this.$emit('expire')
			this.widget?.start()
		})
	},
	beforeUnmount() {
		this.widget?.destroy()
		this.widget = null
		this.sdk = null
	},
	methods: {
		reset() {
			this.widget?.reset()
		},
	},
}
</script>
